import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import { useBugReporter, useDirectBackend } from "#imports";
import type { SalesChannel } from "~/domain/sales-channels";

// Define the state interface
type IState = {
  isLoaded: boolean;
  isEnabled: boolean;
  service: "native" | "deliverect";
  expirationDays: number;
  orderAmountPercentageWorth: number;
  maximumRedeemedOrderAmount: number;
  maximumRedeemedOrderPercentage: number;
  signupPoints: number;
  canRedeemCash: boolean;
  canRedeemPoints: boolean;
  preSignupRequired: boolean;
  signupUrl?: string;
  outletId?: string;
  salesChannel?: string;
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    isEnabled: false,
    service: "native",
    expirationDays: 0,
    orderAmountPercentageWorth: 0,
    maximumRedeemedOrderAmount: 0,
    maximumRedeemedOrderPercentage: 0,
    signupPoints: 0,
    canRedeemCash: false,
    canRedeemPoints: false,
    preSignupRequired: false,
    signupUrl: undefined,
    outletId: undefined,
    salesChannel: undefined,
  };
}

export const useLoyaltyProgramStore = defineStore("loyaltyProgram", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const isNative = computed(() => state.service === "native");

  const { report } = useBugReporter();

  const load = async (request: {
    businessId: string;
    outletId?: string;
    salesChannel: SalesChannel;
  }) => {
    try {
      const { isEnabled, settings: loyaltySettings } =
        await useDirectBackend().loyaltyRepo.getSettings({
          businessId: request.businessId,
          outletId: request.outletId,
          salesChannel: request.salesChannel,
        });

      patch({
        isEnabled,
        outletId: request.outletId,
        salesChannel: request.salesChannel,
        ...(loyaltySettings && {
          ...loyaltySettings,
          signupUrl: loyaltySettings.signupUrl ?? undefined,
        }),
      });
    } catch (error) {
      report(error);
    } finally {
      patch({ isLoaded: true });
    }
  };

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    isNative,
    load,
  };
});
